import React, { useState, useEffect } from 'react';
import StudentLayout from '../../components/StudentLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Button
} from '@mui/material';
import {
  CheckCircle as PaidIcon,
  Pending as PendingIcon,
  Error as FailedIcon,
  MonetizationOn as MoneyIcon
} from '@mui/icons-material';
import axios from 'axios';
import PayButton from '../../components/PayButton';

const StudentPayments = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);
  const [upcomingFee, setUpcomingFee] = useState(null);
  const [dues, setDues] = useState([]);
  const [selectedDueIds, setSelectedDueIds] = useState([]);
  const [payNowLoading, setPayNowLoading] = useState(false);
  const [payNowError, setPayNowError] = useState('');
  const [activePaymentId, setActivePaymentId] = useState(null);

  useEffect(() => {
    fetchDues();
    fetchPayments();
  }, []);

  const fetchDues = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/dues', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDues(response.data.dues || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading dues');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/student/payments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPayments(response.data.payments || []);
      setUpcomingFee(response.data.upcoming_fee || null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error loading payments');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', icon: <PendingIcon fontSize="small" /> },
      paid: { color: 'success', icon: <PaidIcon fontSize="small" /> },
      failed: { color: 'error', icon: <FailedIcon fontSize="small" /> },
      refunded: { color: 'info', icon: <MoneyIcon fontSize="small" /> }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={config.color}
        icon={config.icon}
        size="small"
      />
    );
  };

  const calculateTotals = () => {
    const totalPaid = payments
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);
    
    const totalPending = payments
      .filter(p => p.status === 'pending')
      .reduce((sum, p) => sum + parseFloat(p.amount), 0);

    return { totalPaid, totalPending };
  };

  const totals = calculateTotals();

  // Find all pending payments
  const pendingPayments = payments.filter(p => p.status === 'pending');
  const firstPendingPaymentId = pendingPayments.length > 0 ? pendingPayments[0].id : null;

  // Helper to create a payment record before starting Razorpay
  const createPaymentRecord = async (amount, paymentType, paymentMethod, description = '') => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/student/payments', {
        amount,
        payment_type: paymentType,
        payment_method: paymentMethod,
        description,
        status: 'pending'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.payment;
    } catch (err) {
      setPayNowError(err.response?.data?.message || 'Error creating payment record');
      return null;
    }
  };

  // Helper to create a payment record for selected dues
  const createPaymentForDues = async (dueIds, paymentMethod = 'upi') => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/student/payments', {
        due_ids: dueIds,
        payment_method: paymentMethod,
        status: 'pending'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.payment;
    } catch (err) {
      setPayNowError(err.response?.data?.message || 'Error creating payment record');
      return null;
    }
  };

  if (loading) {
    return (
      <StudentLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dues & Payments
        </Typography>

        {/* Fee Due Card — shows arrears + current month breakdown */}
        {upcomingFee && (
          <Card sx={{
            mb: 4,
            border: '2px solid',
            borderColor: upcomingFee.is_overdue ? 'error.main' : 'warning.main',
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                {upcomingFee.is_overdue ? '⚠️ Fee Due (Includes Arrears)' : '📅 Upcoming Fee'}
              </Typography>

              <Alert severity={upcomingFee.is_overdue ? 'error' : 'info'} sx={{ mb: 2 }}>
                {upcomingFee.is_overdue
                  ? 'You have unpaid fees from previous months. Please clear all dues at once.'
                  : upcomingFee.days_until_due === 0
                    ? 'Your fee is due today!'
                    : `Your fee is due in ${upcomingFee.days_until_due} day${upcomingFee.days_until_due !== 1 ? 's' : ''}.`
                }
              </Alert>

              {/* Breakdown table */}
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Month</strong></TableCell>
                      <TableCell align="right"><strong>Fee</strong></TableCell>
                      <TableCell align="right"><strong>Penalty</strong></TableCell>
                      <TableCell align="right"><strong>Total</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upcomingFee.months_breakdown.map((entry) => (
                      <TableRow key={`${entry.for_year}-${entry.for_month}`}>
                        <TableCell>
                          {entry.month_name}
                          {entry.is_arrear && (
                            <Chip label="Arrear" size="small" color="error" sx={{ ml: 1, fontSize: '0.65rem' }} />
                          )}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(entry.amount)}</TableCell>
                        <TableCell align="right" sx={{ color: entry.penalty_amount > 0 ? 'error.main' : 'text.secondary' }}>
                          {entry.penalty_amount > 0 ? formatCurrency(entry.penalty_amount) : '—'}
                        </TableCell>
                        <TableCell align="right"><strong>{formatCurrency(entry.total)}</strong></TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell colSpan={3}><strong>Total Amount Due</strong></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={upcomingFee.is_overdue ? 'error.main' : 'primary.main'} variant="h6">
                          {formatCurrency(upcomingFee.total_amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color={upcomingFee.is_overdue ? 'error' : 'primary'}
                  size="large"
                  disabled={payNowLoading}
                  onClick={async () => {
                    setPayNowLoading(true);
                    setPayNowError('');
                    try {
                      const token = localStorage.getItem('access_token');
                      const response = await axios.post('/api/student/payments', {
                        months: upcomingFee.months_breakdown.map(e => ({
                          for_month: e.for_month,
                          for_year: e.for_year,
                          amount: e.amount,
                          penalty_amount: e.penalty_amount,
                        })),
                        payment_method: 'upi',
                      }, { headers: { Authorization: `Bearer ${token}` } });

                      const { payment_id, payment_ids, total_amount } = response.data;
                      if (payment_id) {
                        window.dispatchEvent(new CustomEvent('startRazorpayPayment', {
                          detail: { amount: total_amount, paymentId: payment_id, paymentIds: payment_ids }
                        }));
                      }
                    } catch (err) {
                      setPayNowError(err.response?.data?.message || 'Error initiating payment');
                    } finally {
                      setPayNowLoading(false);
                    }
                  }}
                >
                  {payNowLoading ? 'Processing...' : `Pay Now — ${formatCurrency(upcomingFee.total_amount)}`}
                </Button>
              </Box>

              {payNowError && (
                <Alert severity="error" sx={{ mt: 2 }}>{payNowError}</Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Dues Table */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
              Pending Dues
            </Typography>
            {dues.length === 0 ? (
              <Typography variant="body1" sx={{ color: '#8B7355' }} align="center" py={4}>
                No dues found.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell><strong>Description</strong></TableCell>
                      <TableCell><strong>Due Date</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dues.map((due) => (
                      <TableRow key={due.id} hover>
                        <TableCell>
                          {due.status === 'pending' && (
                            <input
                              type="checkbox"
                              checked={selectedDueIds.includes(due.id)}
                              onChange={e => {
                                if (e.target.checked) {
                                  setSelectedDueIds([...selectedDueIds, due.id]);
                                } else {
                                  setSelectedDueIds(selectedDueIds.filter(id => id !== due.id));
                                }
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{due.due_type}</TableCell>
                        <TableCell>{formatCurrency(due.amount)}</TableCell>
                        <TableCell>{getStatusChip(due.status)}</TableCell>
                        <TableCell>{due.description}</TableCell>
                        <TableCell>{due.due_date ? new Date(due.due_date).toLocaleDateString('en-IN') : ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {/* Pay Now for selected dues */}
            {selectedDueIds.length > 0 && (
              <Box sx={{ my: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={payNowLoading}
                  onClick={async () => {
                    setPayNowLoading(true);
                    setPayNowError('');
                    const payment = await createPaymentForDues(selectedDueIds, 'upi');
                    setPayNowLoading(false);
                    console.log('Dispatching startRazorpayPayment', payment);
                    if (payment && payment.id) {
                      window.dispatchEvent(new CustomEvent('startRazorpayPayment', {
                        detail: {
                          amount: payment.amount,  // Amount in rupees - backend converts to paise
                          student: {},
                          paymentId: payment.id
                        }
                      }));
                      setSelectedDueIds([]);
                      fetchDues();
                      fetchPayments();
                    }
                  }}
                >
                  {payNowLoading ? <CircularProgress size={24} /> : `Pay Selected Dues`}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Payment History Table */}
        <Card sx={{
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 100%)',
          border: '1px solid',
          borderColor: 'rgba(212, 175, 55, 0.2)',
          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '4px',
            height: '100%',
            background: 'linear-gradient(180deg, #D4AF37 0%, #F4D03F 100%)',
          },
        }}>
          <CardContent sx={{ pl: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2C2416' }} gutterBottom>
              Transaction History
            </Typography>
            {payments.length === 0 ? (
              <Typography variant="body1" sx={{ color: '#8B7355' }} align="center" py={4}>
                No payment records found.
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Amount</strong></TableCell>
                      <TableCell><strong>Method</strong></TableCell>
                      <TableCell><strong>Transaction ID</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id} hover>
                        <TableCell>
                          {new Date(payment.created_at).toLocaleDateString('en-IN')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.payment_type.replace('_', ' ')}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(payment.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.payment_method.replace('_', ' ')}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                            {payment.transaction_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>{getStatusChip(payment.status)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {totals.totalPending > 0 && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            You have {formatCurrency(totals.totalPending)} in pending payments. Please contact the academy for payment details.
          </Alert>
        )}
      </Box>
      {/* Hidden PayButton to enable Razorpay event listener */}
      <PayButton hidden />
    </StudentLayout>
  );
};

export default StudentPayments;
